import { Component, OnDestroy, OnInit, ViewEncapsulation, effect } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/@core/navigation/navigation.service';
import { APP_PLATFORM_TYPES, Navigation } from 'app/@core/navigation/navigation.types';
import { LayoutStateService } from 'app/@core/services/ui/layout-state.service';
import { TenantSettingsService } from 'app/@core/services/utility/tenant-settings.service';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SettingsComponent } from 'app/layout/common/settings/settings.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { COMPANY_CONFIG } from '@aveespa/customizations/config/company.config';
import { OfflineBannerComponent } from 'app/@core/aveespa-ui/offline-banner/offline-banner.component';
import { MobileTabBarComponent } from 'app/layout/common/mobile-tab-bar/mobile-tab-bar.component';
import { Subject, filter, startWith, takeUntil } from 'rxjs';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        FuseFullscreenComponent,
        NotificationsComponent,
        SettingsComponent,
        UserComponent,
        RouterOutlet,
        OfflineBannerComponent,
        MobileTabBarComponent,
    ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    /** True when the deepest active route opts out of the global mobile tab bar (see requests.routes.ts). */
    hideMobileTabBar = false;
    navigation: Navigation;
    navigationAppearance: 'default' | 'dense' = 'dense';
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    readonly branding = this._tenantSettings.branding;

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _layoutStateService: LayoutStateService,
        private _tenantSettings: TenantSettingsService,
        private _title: Title,
    ) {
        effect(() => {
            this._title.setTitle(this.branding()?.appName ?? COMPANY_CONFIG.appName);
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Recompute whenever navigation finishes (and once immediately for the route
        // active at load) whether the deepest activated route opts out of the global
        // mobile tab bar — see requests.routes.ts for the flag this reads.
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                startWith(null),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe(() => {
                let route = this._activatedRoute;
                while (route.firstChild) {
                    route = route.firstChild;
                }
                this.hideMobileTabBar = !!route.snapshot.data['hideMobileTabBar'];
            });

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Change the navigation appearance
                this.navigationAppearance = this.isScreenSmall
                    ? 'default'
                    : 'dense';
                // Update layout state service
                this._layoutStateService.updateLayoutState({
                    isScreenSmall: this.isScreenSmall,
                    navigationAppearance: this.navigationAppearance,
                    navigationOpened: !this.isScreenSmall
                });
                // Inform the navigation service which “device type” to use
                this._navigationService.updateDeviceType(
                    this.isScreenSmall
                        ? APP_PLATFORM_TYPES.Mobile   // treat small screen as mobile
                        : APP_PLATFORM_TYPES.Web      // treat wide screen as desktop
                );
            });
        // Subscribe to navigation opened/closed events
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                'mainNavigation'
            );

        if (navigation) {
            navigation.openedChanged
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((opened: boolean) => {
                    this._layoutStateService.updateNavigationOpened(opened);
                });
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Navigate to the dashboard
     */
    navigateToDashboard(): void {
        this._router.navigate(['/dashboard']);
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance =
            this.navigationAppearance === 'default' ? 'dense' : 'default';
        // Update layout state service
        this._layoutStateService.updateNavigationAppearance(this.navigationAppearance);
    }
}
